import React from 'react';

import { faAppleWhole } from '@fortawesome/pro-regular-svg-icons/faAppleWhole';
import { faBookOpen } from '@fortawesome/pro-regular-svg-icons/faBookOpen';
import { faBriefcase } from '@fortawesome/pro-regular-svg-icons/faBriefcase';
import { faChartPieSimple } from '@fortawesome/pro-regular-svg-icons/faChartPieSimple';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { faGraduationCap } from '@fortawesome/pro-regular-svg-icons/faGraduationCap';
import { faHandshakeSimple } from '@fortawesome/pro-regular-svg-icons/faHandshakeSimple';
import { faHouse } from '@fortawesome/pro-regular-svg-icons/faHouse';
import { faKey } from '@fortawesome/pro-regular-svg-icons/faKey';
import { faSackDollar } from '@fortawesome/pro-regular-svg-icons/faSackDollar';
import { faSchoolFlag } from '@fortawesome/pro-regular-svg-icons/faSchoolFlag';
import { faTemperatureList } from '@fortawesome/pro-regular-svg-icons/faTemperatureList';
import { faUsers } from '@fortawesome/pro-regular-svg-icons/faUsers';
import { faInboxFull } from '@fortawesome/pro-solid-svg-icons/faInboxFull';
import { faPenSwirl } from '@fortawesome/pro-solid-svg-icons/faPenSwirl';

import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import getPublicCourseCatalogURL from 'components/Common/LayoutV2/utils/getPublicCourseCatalogURL';
import { EL_VIRTUAL_TOURS, VIRTUAL_TOURS } from 'routes/constants';
import { loginURL as methodTestPrepLoginURL } from 'utils/MethodTestPrep';

const AlumIcon = () => <FontAwesomeIcon icon={faGraduationCap} />;
const AppleThinIcon = () => <FontAwesomeIcon icon={faAppleWhole} />;
const BookIcon = () => <FontAwesomeIcon icon={faBookOpen} />;
const CareersThinIcon = () => <FontAwesomeIcon icon={faBriefcase} />;
const CollegeThinIcon = () => <FontAwesomeIcon icon={faSchoolFlag} />;
const GlobeIcon = () => <FontAwesomeIcon icon={faGlobe} />;
const HandshakeIcon = () => <FontAwesomeIcon icon={faHandshakeSimple} />;
const HomeIcon = () => <FontAwesomeIcon icon={faHouse} />;
const KeyIcon = () => <FontAwesomeIcon icon={faKey} />;
const PartnersIcon = () => <FontAwesomeIcon icon={faUsers} />;
const PieChartIcon = () => <FontAwesomeIcon icon={faChartPieSimple} />;
const PlannerIcon = () => <FontAwesomeIcon icon={faPenSwirl} />;
const ScholarshipsThinIcon = () => <FontAwesomeIcon icon={faSackDollar} />;
const TemperatureListIcon = () => <FontAwesomeIcon icon={faTemperatureList} />;
const InboxFullIcon = () => <FontAwesomeIcon icon={faInboxFull} />;

export const MENU_ICON_MAP_ENH = ({
  isStudent,
  isCounselor,
  isAlumni,
  isMember,
}) => ({
  'side-menu-activities': {
    icon: PlannerIcon,
    color: 'blueDark',
    routes:
      isStudent || isAlumni
        ? [
            '/goals/me',
            '/assessments',
            '/learning',
            '/game-of-life',
            '/senior-survey',
            '/el/assessments',
          ]
        : [],
  },
  'side-menu-admin': {
    icon: KeyIcon,
    color: 'indigoDark',
    routes: isCounselor
      ? [
          '/course-planner',
          '/record-requests/schools',
          '/admin-management',
          '/scope-and-sequence-setup',
          '/el/scope-and-sequence-setup',
          '/data-upload',
          '/internal-events',
          '/district-settings/',
          '/lite/colleague-management',
          '/staff-account-management',
          '/el/staff-account-management',
          '/record-requests',
          '/college-app-config',
          '/rosters/staff-rosters',
          '/el/rosters/staff-rosters',
          '/rosters/student-tags',
          '/el/rosters/student-tags',
          '/survey-bank-management/events',
          '/key-readiness-indicators/configuration/',
        ]
      : [],
    activeCondition: (match, pathname, isMenuActive) => {
      const {
        params: { planId, studentPlanUUID },
      } = match;
      if (
        isMenuActive &&
        pathname.startsWith('/course-planner') &&
        (!!planId || !!studentPlanUUID)
      ) {
        return false;
      }
      return isMenuActive;
    },
  },
  'side-menu-analytics': {
    icon: PieChartIcon,
    color: 'blueSteelDark',
    routes: isCounselor
      ? [
          '/analytics',
          '/el/scope-and-sequence-analytics',
          '/scope-and-sequence-analytics',
          '/survey-analytics',
          '/senior-survey-analytics',
          '/key-readiness-indicators',
          '/report-center',
          '/cte-status-dashboard',
        ]
      : [],
    activeCondition: (_, pathname, isMenuActive) => {
      // The configuration page belongs to admin not analytics
      if (
        isMenuActive &&
        pathname.startsWith('/key-readiness-indicators/configuration')
      ) {
        return false;
      }
      return isMenuActive;
    },
  },
  'side-menu-elementary-analytics': {
    icon: PieChartIcon,
    color: 'blueSteelDark',
    routes: isCounselor ? ['/el/report-center'] : [],
  },
  'side-menu-career': {
    icon: CareersThinIcon,
    color: 'redDark',
    routes: [
      ...(isStudent || isAlumni
        ? [
            '/careers',
            '/student-pathways',
            '/opportunities',
            '/employer-showcase',
            '/profile/edit',
          ]
        : []),
      ...(isMember ? ['/employer-showcase'] : []),
      ...(isCounselor
        ? [
            '/careers',
            '/student-pathways',
            '/opportunities',
            '/opportunity-management',
            '/employer-showcase',
            '/industry-partner-database',
          ]
        : []),
    ],
  },
  'side-menu-college': {
    icon: CollegeThinIcon,
    color: 'orangeDark',
    routes: [
      ...(isStudent || isAlumni
        ? [
            '/schools',
            '/school-profile',
            '/schools/final-list',
            '/schools/admission-stats',
            '/letters-of-recommendation',
            '/college-applications',
            '/enrollment-decision',
          ]
        : []),
      ...(isCounselor
        ? [
            '/schools',
            '/school-profile',
            '/recommendation-letter-portal',
            '/college-applications',
            '/college-events',
          ]
        : []),
    ],
  },
  'side-menu-home': {
    icon: HomeIcon,
    color: 'greenDark',
    routes: ['/dashboard', '/student-list', '/meetings/agenda'],
  },
  'side-menu-network': {
    icon: PartnersIcon,
    color: 'violetDark',
    routes: isCounselor
      ? [
          '/industry-partner-events',
          '/industry-partners',
          '/industry-partner-database',
        ]
      : [],
  },
  'side-menu-scholarships': {
    icon: ScholarshipsThinIcon,
    color: 'purpleDark',
    routes: [
      ...(isStudent
        ? ['/scholarships', '/college-costs', '/scholarship-matching']
        : []),
      ...(isCounselor
        ? [
            '/scholarships',
            '/scholarship-management',
            '/award-letter-verification',
            '/scholarship-matching',
            '/fafsa-tracker',
          ]
        : []),
    ],
  },
  'side-menu-school': {
    icon: AppleThinIcon,
    color: 'tealDark',
    routes: [
      ...(isStudent
        ? [
            '/key-readiness-indicators',
            '/test-scores',
            '/course-plans',
            '/record-requests',
            '/plans',
            '/experience-tracking',
            '/course-catalog',
          ]
        : []),
      ...(isCounselor
        ? [
            '/course-plans',
            '/personalized-plans',
            '/el/personalized-plans',
            '/experience-tracking',
            '/el/experience-tracking',
            '/student-experience-supervision',
            '/course-catalog',
            '/events',
            '/badges',
            '/scope-and-sequence-verification/file-and-forms',
            '/initiative-page/ohio-graduation-requirements',
            '/initiative-page/dodea-transcript-template',
          ]
        : []),
    ],
    activeCondition: (match, pathname, isMenuActive) => {
      const {
        params: { planId, studentPlanUUID },
      } = match;
      if (
        !isMenuActive &&
        pathname.startsWith('/course-planner') &&
        (!!planId || !!studentPlanUUID)
      ) {
        return true;
      }
      return isMenuActive;
    },
  },
  'side-menu-alum': {
    icon: AlumIcon,
    color: 'violetDark',
    routes: isCounselor
      ? [
          '/alumni',
          '/alumni/database',
          '/alumni/national-student-clearinghouse',
        ]
      : [],
  },
  'side-menu-elementary-home': {
    icon: HomeIcon,
    color: 'greenDark',
    routes: ['/el/dashboard', '/el/casefile'],
  },
  'side-menu-checkins': {
    icon: TemperatureListIcon,
    color: 'tealDark',
    routes: [
      '/el/checkins/schedule',
      '/el/checkins/overview',
      '/el/checkins/insights',
      '/el/checkins/settings',
      '/el/checkins/suggested-lessons',
      '/checkins/overview',
      '/checkins/schedule',
      '/checkins/insights',
      '/checkins/settings',
    ],
  },
  'side-menu-lessons': {
    icon: BookIcon,
    color: 'blueDark',
    routes: ['/el/lesson-library'],
  },
  'side-menu-elementary-career': {
    icon: CareersThinIcon,
    color: 'redDark',
    routes: ['/el/career-interest-analytics', '/el/industry-partner-database'],
  },
  'side-menu-programs': {
    icon: HandshakeIcon,
    color: 'pink',
    routes: isCounselor
      ? ['/program-exploration', '/program-management', '/placement-history']
      : [
          '/program-exploration/candidate/all-programs',
          '/program-exploration/candidate/placements',
          '/placement-history',
        ],
  },
  'side-menu-connect': {
    icon: GlobeIcon,
    color: 'blueDark',
    routes: ['/district-connections'],
  },
  'side-menu-requests': {
    icon: InboxFullIcon,
    color: 'blueDark',
    routes: [
      '/recommendation-letter-portal',
      '/student-experience-supervision',
    ],
  },
});

export const MENU_ICON_MAP = {
  'side-menu-activities': PlannerIcon,
  'side-menu-admin': KeyIcon,
  'side-menu-analytics': PieChartIcon,
  'side-menu-elementary-analytics': PieChartIcon,
  'side-menu-career': CareersThinIcon,
  'side-menu-college': CollegeThinIcon,
  'side-menu-home': HomeIcon,
  'side-menu-elementary-home': HomeIcon,
  'side-menu-network': PartnersIcon,
  'side-menu-scholarships': ScholarshipsThinIcon,
  'side-menu-school': AppleThinIcon,
  'side-menu-alum': AlumIcon,
  'side-menu-checkins': TemperatureListIcon,
  'side-menu-programs': HandshakeIcon,
  'side-menu-connect': GlobeIcon,
  'side-menu-lessons': BookIcon,
  'side-menu-requests': InboxFullIcon,
};

export const MENU_ICON_LINK_MAP = {
  'side-menu-home': '/login',
};

export const NAV_LINK_MAP = {
  'side-elementary-accountability-system-analytics':
    '/el/scope-and-sequence-analytics',
  'side-accountability-system-analytics': '/scope-and-sequence-analytics',
  'side-accountability-tracker': '/accountability-tracker',
  'side-admin-management': '/staff-account-management',
  'side-admin-management-elementary': '/el/staff-account-management',
  'side-elementary-indicator-accountability-setup':
    '/el/scope-and-sequence-setup',
  'side-alum-analytics': '/alumni/analytics',
  'side-alum-database': '/alumni/database',
  'side-alum-home': '/alumni',
  'side-alum-survey': '/alumni/surveys',
  'side-nsc': '/alumni/national-student-clearinghouse',
  'side-analytics': '/analytics',
  'side-industry-partner-analytics': '/industry-partner-analytics',
  'side-application-manager': '/college-applications',
  'side-enrollment-decision': '/enrollment-decision',
  'side-indicator-requirement-cluster-ncaa': '/college-athletic-eligibility',
  'side-assessments': '/assessments',
  'side-automation': '/automation',
  'side-elementary-assessments': '/assessments',
  'side-badges': '/badges',
  'side-careers': '/careers',
  'side-elementary-careers': '/el/student/clusters',
  'side-elementary-career-matcher': '/el/student/career-quiz',
  'side-college-apps-settings': '/college-app-config',
  'side-college-cost': '/college-costs',
  'side-college-rep-events': '/college-events',
  'side-community-dashboard': '/industry-partner-dashboard',
  'side-community-user-management': '/community-user-management',
  'side-course-catalog': '/course-planner-setup',
  'side-course-planner': '/course-plans',
  'side-public-course-catalog': getPublicCourseCatalogURL(),
  'side-dashboard': '/dashboard',
  'side-data-upload': '/data-upload/overview',
  'side-district-connections': '/district-connections',
  'side-district-settings': '/district-settings/',
  'side-elementary-experience-tracking': '/el/experience-tracking',
  'side-employer-showcase': '/employer-showcase',
  'side-event-scheduler': '/event-scheduler',
  'side-final-list': '/schools/final-list',
  'side-college-admission-stats': '/schools/admission-stats',
  'side-financial-aid': '/financial-aid',
  'side-survey-bank': '/survey-bank-management',
  'side-game-of-life': '/game-of-life',
  'side-goals': '/goals/me',
  'side-homepage': '/login',
  'side-indicator-accountability-setup': '/scope-and-sequence-setup',
  'side-industry-partner-crm': '/industry-partner-database/contacts',
  'side-elementary-industry-partner-crm':
    '/el/industry-partner-database/contacts',
  'side-industry-partner-programs': '/program-management/programs',
  'side-key-readiness-indicators': '/key-readiness-indicators',
  'side-learning': '/learning',
  'side-elementary-learning': '/el/learning',
  'side-member-management': '/community-user-management',
  'side-member-organization-profile': '/organization-profile',
  'side-method-test-prep-login': methodTestPrepLoginURL,
  'side-my-placements-candidate': '/program-exploration/candidate/placements',
  'side-k12-admin-meeting-scheduler': '/meetings',
  'side-k12-admin-ncaa': '/college-athletic-eligibility/tabular-view',
  'side-opportunities': '/opportunities',
  'side-opportunity-management': '/opportunity-management',
  'side-partner-directory': '/industry-partners',
  'side-personalized-plans': '/personalized-plans', // None student
  'side-elementary-personalized-plans': '/el/personalized-plans',
  'side-placement-history': '/program-management/placement-history',
  'side-program-exploration-candidate':
    '/program-exploration/candidate/all-programs',
  'side-program-exploration-staff': '/program-exploration',
  'side-student-profile': '/profile/edit',
  'side-elementary-virtual-workplace-tours': EL_VIRTUAL_TOURS,
  'side-virtual-workplace-tours': VIRTUAL_TOURS,
  'side-program-exploration-sponsor':
    '/program-exploration/sponsor/all-programs',
  'side-program-management': '/program-management/programs',
  'side-org-program-management': '/program-management',
  'side-district-pathways': '/student-pathways',
  'side-recommendation-letter-portal': '/recommendation-letter-portal',
  'side-recommendation-letter-requests': '/letters-of-recommendation',
  'side-report-center': '/report-center',
  'side-elementary-report-center': '/el/report-center',
  'side-roster-management': '/rosters/student-tags',
  'side-roster-management-elementary': '/el/rosters/staff-rosters',
  'side-scholarship-management': '/scholarship-management',
  'side-scholarship-matching': '/scholarship-matching',
  'side-school-events': '/dashboard/student/school-events',
  'side-school-favorites': '/schools/favorites',
  'side-school-search': '/schools/search',
  'side-student-accountability-tracker': '/accountability-tracker',
  'side-student-key-readiness-indicators': '/key-readiness-indicators',
  'side-student-list': '/student-list',
  'side-student-list-elementary': '/el/student-list',
  'side-student-personalized-plan': '/plans/overview',
  'side-student-transcript-request': '/record-requests/transcript',
  'side-survey-analytics': '/survey-analytics',
  'side-elementary-survey-analytics': '/el/survey-analytics',
  'side-senior-survey-analytics': '/senior-survey-analytics',
  'side-test-scores': '/test-scores',
  'side-transcript-center': '/record-requests',
  'side-transcript-management': '/record-requests/schools',
  'side-transcript-request': '/record-requests/requests',
  'side-upcoming-events': '/events/event-schedule',
  'side-experience-tracking': '/experience-tracking',
  'side-experience-tracking-supervisor': '/student-experience-supervision',
  'side-college-financing': '/award-letter-verification',
  'side-fafsa-tracker': '/fafsa-tracker',
  'side-indicator-verification':
    '/scope-and-sequence-verification/file-and-forms',
  'ohio-graduation-requirements':
    '/initiative-page/ohio-graduation-requirements',
  'dodea-transcript-template': '/initiative-page/dodea-transcript-template',
  'texas-semiconductor-manufacturing-careers':
    '/initiative-page/texas-semiconductor-manufacturing-careers',
  'side-elementary-dashboard': '/el/dashboard',
  'side-student-casefiles': '/el/casefile/',
  'side-checkins-overview': '/el/checkins/overview',
  'side-checkins-schedule': '/el/checkins/schedule',
  'side-checkins-insights': '/el/checkins/insights',
  'side-checkins-settings': '/el/checkins/settings',
  'side-checkins-suggested-lessons': '/el/checkins/suggested-lessons/redirect',
  'side-lesson-library': '/lesson-library/redirect',
  'side-career-interest': '/el/career-interest-analytics',
  'side-secondary-checkins-overview': '/checkins/overview',
  'side-secondary-checkins-schedule': '/checkins/schedule',
  'side-secondary-checkins-insights': '/checkins/insights',
  'side-secondary-checkins-settings': '/checkins/settings',
  'side-accountability-tracking-config':
    '/key-readiness-indicators/configuration/',
  'side-cte-status-dashboard': '/cte-status-dashboard',
};
